import React, { useState } from 'react';
import { db } from '../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useDarkMode } from '../context/DarkModeContext';
import { getTheme } from '../theme';

const STEPS = [
  {
    id: 'welcome',
    emoji: '👋',
    title: "Welcome to Learnable",
    body: "You're about to have the most personalised study experience of your life. Let's take 60 seconds to show you exactly how it works — and why it works.",
    action: "Show me around",
    why: "Research shows students who identify their knowledge gaps before studying retain 40% more. This tour sets you up to use every tool correctly.",
  },
  {
    id: 'subjects',
    emoji: '📚',
    title: "Add your subjects",
    body: "Click '+ Add Subject' to add every subject you're studying. Learnable will instantly map your full curriculum — every unit, topic, and subtopic — so nothing gets missed.",
    action: "Got it",
    target: 'add-subject-btn',
    why: "Having all your subjects mapped means Learnable can intelligently prioritise across all of them — not just one at a time.",
  },
  {
    id: 'gapmap',
    emoji: '🗺️',
    title: "Rate your confidence",
    body: "Traffic-light every subtopic: green = confident, amber = shaky, red = gap. This 5-minute exercise transforms everything — Learnable knows exactly what you need to work on, not just what you haven't touched.",
    action: "Makes sense",
    why: "The traffic-light system is based on metacognition research — knowing what you don't know is the #1 predictor of exam success.",
  },
  {
    id: 'direction',
    emoji: '🎯',
    title: "Always know what to study",
    body: "Your Dashboard shows exactly what to do today — your weakest topics first, sorted by exam urgency. No more guessing. Cailyn builds you a day-by-day plan around your actual gaps.",
    action: "Brilliant",
    why: "Students with a clear daily study plan are 3× more likely to reach their target grade than those who study without direction.",
  },
  {
    id: 'ai',
    emoji: '🤖',
    title: "Your personal AI tutor",
    body: "Cailyn knows your exact gaps, your learning style, and your exam dates. She explains concepts in your language, generates questions targeting your weak spots, and never judges you for asking the same thing twice.",
    action: "Amazing",
    why: "Personalised tutoring adapts to your specific gaps — proven to be 2× more effective than working through generic content.",
  },
  {
    id: 'planner',
    emoji: '📅',
    title: "Your study plan",
    body: "Go to Study Planner, add your exam dates, and Learnable generates a complete day-by-day schedule built around your gaps and your timeline. Preparing for SAT or ACT? Add those dates for a dedicated prep roadmap.",
    action: "Start learning →",
    final: true,
    why: "Students who set exam dates and follow a structured plan score on average 18% higher than those who study without a schedule.",
  },
];

export default function OnboardingTour({ onComplete }) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(true);
  const { user } = useAuth();
  const { isDark } = useDarkMode();
  const t = getTheme(isDark);

  const current = STEPS[step];

  const next = async () => {
    if (step >= STEPS.length - 1 || current.final) {
      setVisible(false);
      if (user) {
        try {
          await updateDoc(doc(db, 'users', user.uid), { isNewUser: false });
        } catch(e) {}
      }
      onComplete?.();
      return;
    }
    setStep(s => s + 1);
  };

  const skip = async () => {
    setVisible(false);
    if (user) {
      try {
        await updateDoc(doc(db, 'users', user.uid), { isNewUser: false });
      } catch(e) {}
    }
    onComplete?.();
  };

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: isDark ? 'rgba(14,12,10,0.88)' : 'rgba(250,250,247,0.88)',
      backdropFilter: 'blur(8px)',
    }}>
      <div style={{
        width: '100%', maxWidth: '500px', margin: '0 20px',
        backgroundColor: t.surface,
        border: `1px solid ${t.border2}`,
        borderRadius: '24px', padding: '36px',
        boxShadow: isDark ? '0 32px 80px rgba(0,0,0,0.6)' : '0 32px 80px rgba(0,0,0,0.12)',
        fontFamily: "'DM Sans',-apple-system,sans-serif",
      }}>

        {/* Progress bar */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '28px', justifyContent: 'center' }}>
          {STEPS.map((s, i) => (
            <div key={i} style={{
              height: '4px', borderRadius: '2px', transition: 'all 0.3s',
              backgroundColor: i <= step ? t.gold : t.border,
              width: i === step ? '28px' : '8px',
            }} />
          ))}
        </div>

        {/* Emoji */}
        <div style={{
          width: '72px', height: '72px', borderRadius: '20px',
          backgroundColor: t.goldGlow,
          border: `1px solid ${t.goldBorder}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '36px', margin: '0 auto 20px',
        }}>
          {current.emoji}
        </div>

        {/* Content */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.1em', color: t.gold, marginBottom: '10px' }}>
            Step {step + 1} of {STEPS.length}
          </div>
          <h2 style={{ fontSize: '22px', fontWeight: '800', color: t.text, marginBottom: '12px', lineHeight: 1.3 }}>
            {current.title}
          </h2>
          <p style={{ fontSize: '14px', color: t.textSec, lineHeight: 1.7 }}>
            {current.body}
          </p>
        </div>

        {/* Science callout */}
        <div style={{
          background: t.surface2,
          border: `1px solid ${t.border}`,
          borderRadius: '12px', padding: '12px 16px', marginBottom: '20px',
          display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
          <span style={{ fontSize: 15, flexShrink: 0 }}>💡</span>
          <div style={{ fontSize: '12px', color: t.textSec, lineHeight: 1.6 }}>
            {current.why}
          </div>
        </div>

        {/* Actions */}
        <button
          onClick={next}
          style={{
            width: '100%', padding: '14px', borderRadius: '12px',
            fontSize: '15px', fontWeight: '700',
            background: `linear-gradient(135deg, ${t.gold}, ${t.goldDim})`,
            color: '#0e0c0a', border: 'none', cursor: 'pointer',
            marginBottom: '10px',
            boxShadow: `0 4px 20px ${t.goldGlowStrong}`,
          }}>
          {current.action} {current.final ? '🚀' : ''}
        </button>
        <button
          onClick={skip}
          style={{
            width: '100%', padding: '10px', borderRadius: '12px',
            fontSize: '13px', fontWeight: '500',
            backgroundColor: 'transparent', border: 'none',
            color: t.textTert, cursor: 'pointer',
          }}>
          Skip tour
        </button>
      </div>
    </div>
  );
}
